import { useEffect, useState, useMemo } from 'react';
import appConstants from '../constants/appConstants';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Layout from '../components/Layout';
import { useAppContext } from '../context/AppContext';
import axios from 'axios';
import { Form, Button, Table, Spinner, Alert, Badge } from 'react-bootstrap';
import {
  FaMoneyCheckAlt,
  FaPrint,
  FaFileExcel,
  FaSearch,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaLayerGroup,
  FaUserCheck,
} from 'react-icons/fa';

const getAuthToken = () => localStorage.getItem('authToken');

const StudentFeeSummary = () => {
  const { selectedAcademicYear, institute } = useAppContext();
  const [batchId, setBatchId] = useState('');
  const [status, setStatus] = useState('Active');
  const [batches, setBatches] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });

  useEffect(() => {
    loadBatches();
  }, [selectedAcademicYear]);

  const loadBatches = async () => {
    const token = getAuthToken();

    if (!token) {
      setErrorMsg('Unauthorized! Please log in.');
      setBatches([]);
      return;
    }

    if (!selectedAcademicYear || !selectedAcademicYear.id) {
      setBatches([]);
      setBatchId('');
      return;
    }

    try {
      const res = await axios.get(
        `${appConstants.API_ROOT_URL}get-batches-v3`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );
      if (res.data.status === 'success') {
        setBatches(res.data.data || []);
      } else {
        setErrorMsg('Failed to load batches.');
      }
    } catch (err) {
      setBatches([]);
      setErrorMsg('Error loading batches.');
    }
  };

  const fetchStudents = async () => {
    if (!selectedAcademicYear?.id) {
      setErrorMsg('Please select an academic year.');
      setStudents([]);
      return;
    }

    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.post(
        `${appConstants.API_ROOT_URL}get-students-fee-summary`,
        {
          batch_id: batchId,
          status,
        },
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
          params: { academic_year_id: selectedAcademicYear.id },
        },
      );
      if (res.data.status === 'success') {
        setStudents(res.data.data || []);
      } else {
        setErrorMsg(res.data.message || 'Failed to load students.');
      }
    } catch (err) {
      setErrorMsg('Error fetching student data.');
    } finally {
      setLoading(false);
    }
  };

  // Selected batch information
  const selectedBatch = useMemo(() => {
    return batches.find((b) => String(b.id) === String(batchId));
  }, [batches, batchId]);

  const batchDisplayName = selectedBatch
    ? `${selectedBatch.class_name || ''} ${selectedBatch.division || ''}`.trim()
    : 'All Batches';

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Sorted students list
  const sortedStudents = useMemo(() => {
    if (!sortConfig.key) return students;

    return [...students].sort((a, b) => {
      let aVal;
      let bVal;

      switch (sortConfig.key) {
        case 'admission_no':
          aVal = a.admission_no || '';
          bVal = b.admission_no || '';
          const numA = Number(aVal);
          const numB = Number(bVal);
          if (!isNaN(numA) && !isNaN(numB)) {
            return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
          }
          return sortConfig.direction === 'asc'
            ? String(aVal).localeCompare(String(bVal), undefined, { numeric: true })
            : String(bVal).localeCompare(String(aVal), undefined, { numeric: true });

        case 'student_name':
          aVal = (a.student_name || '').trim();
          bVal = (b.student_name || '').trim();
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal, undefined, { sensitivity: 'base' })
            : bVal.localeCompare(aVal, undefined, { sensitivity: 'base' });

        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          return sortConfig.direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);

        case 'total_fees':
          aVal = parseFloat(a.total_fees || 0);
          bVal = parseFloat(b.total_fees || 0);
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;

        case 'due_fees':
          aVal = parseFloat(a.due_fees || 0);
          bVal = parseFloat(b.due_fees || 0);
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;

        case 'paid_fees':
          aVal = parseFloat(a.paid_fees || 0);
          bVal = parseFloat(b.paid_fees || 0);
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;

        case 'due_balance':
          aVal = parseFloat(a.due_fees || 0) - parseFloat(a.paid_fees || 0);
          bVal = parseFloat(b.due_fees || 0) - parseFloat(b.paid_fees || 0);
          return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;

        default:
          return 0;
      }
    });
  }, [students, sortConfig]);

  // Calculated totals
  const totals = useMemo(() => {
    return students.reduce(
      (acc, stu) => {
        const total = parseFloat(stu.total_fees || 0);
        const due = parseFloat(stu.due_fees || 0);
        const paid = parseFloat(stu.paid_fees || 0);
        const balance = due - paid;
        return {
          totalFees: acc.totalFees + total,
          dueFees: acc.dueFees + due,
          paidFees: acc.paidFees + paid,
          dueBalance: acc.dueBalance + balance,
        };
      },
      { totalFees: 0, dueFees: 0, paidFees: 0, dueBalance: 0 },
    );
  }, [students]);

  // Print handler
  const handlePrint = () => {
    window.print();
  };

  // Download Excel handler
  const handleDownloadExcel = () => {
    if (!sortedStudents.length) return;
    const header = [
      'Sl',
      'Ad.No',
      'Name',
      'Status',
      'Total Fees',
      'Due',
      'Paid Fees',
      'Due Balance',
    ];
    const rows = sortedStudents.map((stu, idx) => [
      idx + 1,
      stu.admission_no,
      stu.student_name,
      stu.status,
      parseFloat(stu.total_fees || 0).toFixed(2),
      parseFloat(stu.due_fees || 0).toFixed(2),
      parseFloat(stu.paid_fees || 0).toFixed(2),
      (parseFloat(stu.due_fees || 0) - parseFloat(stu.paid_fees || 0)).toFixed(2),
    ]);
    const csvContent = [header, ...rows]
      .map((row) =>
        row
          .map((field) => '"' + String(field).replace(/"/g, '""') + '"')
          .join(','),
      )
      .join('\r\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `student_fee_summary_${batchDisplayName.replace(/\s+/g, '_') || 'batch'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Render sort direction icon
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <FaSort
          className="sort-icon text-muted ms-1 no-print"
          style={{ opacity: 0.35, fontSize: '0.72rem' }}
        />
      );
    }
    if (sortConfig.direction === 'asc') {
      return (
        <FaSortUp
          className="sort-icon text-primary ms-1 no-print"
          style={{ fontSize: '0.72rem' }}
        />
      );
    }
    return (
      <FaSortDown
        className="sort-icon text-primary ms-1 no-print"
        style={{ fontSize: '0.72rem' }}
      />
    );
  };

  return (
    <Layout>
      <style>{`
        /* Topbar Container */
        .fee-summary-header-card {
          background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
          color: #f8fafc;
          border-radius: 12px;
          padding: 1.15rem 1.25rem;
          margin-bottom: 20px;
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.18);
        }

        /* Top Bar Header Row */
        .fee-summary-header-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 12px;
          padding-bottom: 1rem;
          margin-bottom: 1rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .fee-summary-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .fee-summary-icon-box {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.4);
          color: #60a5fa;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.2rem;
          flex-shrink: 0;
        }

        .fee-summary-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: #ffffff;
          margin: 0;
          letter-spacing: -0.2px;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .fee-summary-badges {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
        }

        .fee-summary-top-badge {
          background: rgba(255, 255, 255, 0.1);
          color: #cbd5e1;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 4px 9px;
          border-radius: 6px;
          border: 1px solid rgba(255, 255, 255, 0.12);
        }

        /* Action Buttons */
        .fee-summary-header-actions {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .fee-btn-action-excel {
          background: rgba(16, 185, 129, 0.15) !important;
          color: #34d399 !important;
          border: 1px solid rgba(52, 211, 153, 0.35) !important;
          font-size: 0.85rem !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
          padding: 6px 14px !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          transition: all 0.2s ease !important;
        }
        .fee-btn-action-excel:hover {
          background: #10b981 !important;
          color: #ffffff !important;
          transform: translateY(-1px);
        }

        .fee-btn-action-print {
          background: rgba(255, 255, 255, 0.12) !important;
          color: #ffffff !important;
          border: 1px solid rgba(255, 255, 255, 0.25) !important;
          font-size: 0.85rem !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
          padding: 6px 14px !important;
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          transition: all 0.2s ease !important;
        }
        .fee-btn-action-print:hover {
          background: #ffffff !important;
          color: #0f172a !important;
          transform: translateY(-1px);
        }

        /* Controls / Filter Bar */
        .fee-summary-controls-grid {
          display: flex;
          align-items: flex-end;
          gap: 12px;
          flex-wrap: wrap;
        }

        .fee-filter-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1 1 200px;
          min-width: 160px;
        }

        .fee-filter-field label {
          font-size: 0.78rem;
          font-weight: 600;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .fee-custom-select {
          background-color: #334155;
          border: 1px solid #475569;
          color: #f8fafc;
          font-size: 0.875rem;
          font-weight: 500;
          border-radius: 8px;
          height: 38px;
          padding: 0 10px;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .fee-custom-select:focus {
          background-color: #334155;
          border-color: #3b82f6;
          color: #ffffff;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.25);
        }

        .fee-btn-fetch {
          background: #2563eb !important;
          border: 1px solid #3b82f6 !important;
          color: #ffffff !important;
          font-size: 0.875rem !important;
          font-weight: 600 !important;
          border-radius: 8px !important;
          height: 38px !important;
          padding: 0 18px !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 7px !important;
          flex: 0 0 auto;
          transition: all 0.2s ease !important;
        }
        .fee-btn-fetch:hover:not(:disabled) {
          background: #1d4ed8 !important;
          border-color: #2563eb !important;
          transform: translateY(-1px);
        }

        /* Screen Table Styling */
        .fee-summary-table-container {
          background-color: #fff;
          border-radius: 10px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.06);
          border: 1px solid #e2e8f0;
          overflow: hidden;
        }
        .fee-summary-table {
          margin-bottom: 0;
          font-size: 0.88rem;
        }
        .fee-summary-table thead th {
          background: #f8fafc;
          border-bottom: 2px solid #cbd5e1;
          font-weight: 600;
          color: #334155;
          padding: 9px 10px;
          white-space: nowrap;
          vertical-align: middle;
        }
        .fee-summary-table thead th.sortable-th {
          cursor: pointer;
          user-select: none;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        .fee-summary-table thead th.sortable-th:hover {
          background-color: #f1f5f9;
          color: #2563eb;
        }
        .fee-summary-table thead th.sortable-th.active-sort {
          background-color: #eff6ff;
          color: #1d4ed8;
        }
        .fee-summary-table tbody td {
          padding: 7px 10px;
          vertical-align: middle;
        }
        .fee-summary-table tfoot td,
        .fee-summary-table tfoot th {
          background: #f8fafc;
          font-weight: 700;
          padding: 9px 10px;
          border-top: 2px solid #cbd5e1;
          vertical-align: middle;
        }

        /* Column Specific Screen Widths and Alignments */
        .col-sl { width: 45px; text-align: center; }
        .col-adno { width: 95px; text-align: center; font-weight: 500; white-space: nowrap; }
        .col-name { text-align: left; font-weight: 500; }
        .col-status { width: 85px; text-align: center; }
        .col-total { width: 110px; text-align: right; font-weight: 600; white-space: nowrap; }
        .col-due { width: 100px; text-align: right; font-weight: 600; white-space: nowrap; }
        .col-paid { width: 100px; text-align: right; font-weight: 600; white-space: nowrap; }
        .col-balance { width: 110px; text-align: right; font-weight: 700; white-space: nowrap; }

        .th-content {
          display: inline-flex;
          align-items: center;
          gap: 2px;
        }
        .th-content-right {
          display: inline-flex;
          align-items: center;
          justify-content: flex-end;
          gap: 2px;
          width: 100%;
        }
        .th-content-center {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 2px;
          width: 100%;
        }

        .fee-status-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.76rem;
          font-weight: 500;
        }
        .fee-status-active {
          background-color: #dcfce7;
          color: #166534;
        }
        .fee-status-suspend {
          background-color: #fee2e2;
          color: #991b1b;
        }

        /* Print Header - Hidden on Screen */
        .fee-summary-print-header {
          display: none;
        }

        /* Mobile & Responsive Tweaks */
        @media (max-width: 768px) {
          .fee-summary-header-card {
            padding: 1rem;
          }
          .fee-summary-header-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
          .fee-summary-header-actions {
            width: 100%;
            justify-content: flex-start;
          }
          .fee-summary-controls-grid {
            flex-direction: column;
            align-items: stretch;
            gap: 10px;
          }
          .fee-filter-field {
            width: 100%;
            min-width: 100%;
          }
          .fee-btn-fetch {
            width: 100%;
          }
        }

        /* PRINT OPTIMIZATION FOR MAXIMUM STUDENTS PER PAGE */
        @media print {
          @page {
            size: A4 portrait;
            margin: 8mm 6mm 8mm 6mm;
          }
          *, *::before, *::after {
            box-shadow: none !important;
            text-shadow: none !important;
          }
          body {
            background: #fff !important;
            color: #000 !important;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * {
            visibility: hidden !important;
          }
          #fee-summary-print-area, #fee-summary-print-area * {
            visibility: visible !important;
          }
          #fee-summary-print-area {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            background: #fff !important;
          }
          .no-print, .no-print *, .sort-icon {
            display: none !important;
          }

          /* Compact Printable Header */
          .fee-summary-print-header {
            display: block !important;
            text-align: center;
            margin-bottom: 5px !important;
            padding-bottom: 3px !important;
            border-bottom: 1.5px solid #000 !important;
          }
          .fee-print-inst-name {
            font-size: 13pt !important;
            font-weight: 800 !important;
            text-transform: uppercase;
            margin: 0 0 1px 0 !important;
            line-height: 1.15 !important;
            color: #000 !important;
          }
          .fee-print-title {
            font-size: 10.5pt !important;
            font-weight: 700 !important;
            letter-spacing: 0.5px;
            margin: 0 0 3px 0 !important;
            line-height: 1.15 !important;
            text-transform: uppercase;
            color: #000 !important;
          }
          .fee-print-meta-row {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            font-size: 8pt !important;
            line-height: 1.2 !important;
            color: #111 !important;
          }
          .fee-print-meta-item {
            font-weight: 500 !important;
          }
          .fee-print-meta-item strong {
            font-weight: 700 !important;
          }

          /* High Density Printable Table */
          .fee-summary-table-container {
            border-radius: 0 !important;
            box-shadow: none !important;
            background: transparent !important;
            border: none !important;
            overflow: visible !important;
          }
          .table-responsive {
            overflow: visible !important;
            display: block !important;
          }
          .fee-summary-table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 8.5pt !important;
            line-height: 1.15 !important;
            margin: 0 !important;
          }
          .fee-summary-table thead {
            display: table-header-group !important;
          }
          .fee-summary-table tfoot {
            display: table-footer-group !important;
          }
          .fee-summary-table tr {
            page-break-inside: avoid !important;
            page-break-after: auto !important;
            height: auto !important;
          }
          .fee-summary-table th,
          .fee-summary-table td {
            border: 0.75pt solid #333 !important;
            padding: 2px 4px !important;
            color: #000 !important;
            vertical-align: middle !important;
          }
          .fee-summary-table thead th {
            background-color: #f0f0f0 !important;
            font-weight: 700 !important;
            font-size: 8.5pt !important;
            padding: 3px 4px !important;
          }
          .fee-summary-table tfoot td,
          .fee-summary-table tfoot th {
            background-color: #f2f2f2 !important;
            font-weight: 700 !important;
            border-top: 1.5pt solid #000 !important;
            border-bottom: 1.5pt solid #000 !important;
            padding: 3px 4px !important;
            font-size: 8.5pt !important;
          }

          /* Print Column Widths for Optimal Density */
          .col-sl {
            width: 26px !important;
            text-align: center !important;
            padding-left: 1px !important;
            padding-right: 1px !important;
          }
          .col-adno {
            width: 68px !important;
            text-align: center !important;
            white-space: nowrap !important;
          }
          .col-name {
            text-align: left !important;
            font-weight: 600 !important;
          }
          .col-status {
            width: 52px !important;
            text-align: center !important;
          }
          .col-total {
            width: 78px !important;
            text-align: right !important;
            white-space: nowrap !important;
          }
          .col-due {
            width: 74px !important;
            text-align: right !important;
            white-space: nowrap !important;
          }
          .col-paid {
            width: 74px !important;
            text-align: right !important;
            white-space: nowrap !important;
          }
          .col-balance {
            width: 80px !important;
            text-align: right !important;
            white-space: nowrap !important;
            font-weight: 700 !important;
          }

          /* Remove pill badge bulky padding in print */
          .fee-status-badge {
            background: none !important;
            color: #000 !important;
            padding: 0 !important;
            font-size: 8pt !important;
            font-weight: 500 !important;
            border: none !important;
          }
        }
      `}</style>

      <div className="container-fluid px-3 px-md-4 py-3">
        {/* Modern Butter Top Bar */}
        <div className="fee-summary-header-card no-print">
          {/* Header Top Row: Brand & Actions */}
          <div className="fee-summary-header-row">
            <div className="fee-summary-brand">
              <div className="fee-summary-icon-box">
                <FaMoneyCheckAlt />
              </div>
              <div>
                <h1 className="fee-summary-title">Student Fee Summary</h1>
              </div>
              <div className="fee-summary-badges">
                {selectedAcademicYear?.name && (
                  <span className="fee-summary-top-badge">
                    {selectedAcademicYear.name}
                  </span>
                )}
                {batchId && selectedBatch && (
                  <span className="fee-summary-top-badge">
                    {batchDisplayName}
                  </span>
                )}
              </div>
            </div>

            {students.length > 0 && (
              <div className="fee-summary-header-actions">
                <Button
                  onClick={handleDownloadExcel}
                  className="fee-btn-action-excel"
                  size="sm"
                >
                  <FaFileExcel />
                  <span>Excel</span>
                </Button>
                <Button
                  onClick={handlePrint}
                  className="fee-btn-action-print"
                  size="sm"
                >
                  <FaPrint />
                  <span>Print</span>
                </Button>
              </div>
            )}
          </div>

          {/* Header Controls Row: Filters & Fetch */}
          <div className="fee-summary-controls-grid">
            <div className="fee-filter-field">
              <label>
                <FaLayerGroup /> Batch
              </label>
              <Form.Select
                value={batchId}
                onChange={(e) => setBatchId(e.target.value)}
                size="sm"
                className="fee-custom-select"
              >
                <option value="" style={{ color: '#000' }}>
                  Select Batch
                </option>
                {batches.map((batch) => (
                  <option
                    key={batch.id}
                    value={batch.id}
                    style={{ color: '#000' }}
                  >
                    {batch.class_name} {batch.division}
                  </option>
                ))}
              </Form.Select>
            </div>

            <div className="fee-filter-field">
              <label>
                <FaUserCheck /> Status
              </label>
              <Form.Select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                size="sm"
                className="fee-custom-select"
              >
                <option value="Active" style={{ color: '#000' }}>
                  Active
                </option>
                <option value="Suspend" style={{ color: '#000' }}>
                  Suspend
                </option>
              </Form.Select>
            </div>

            <Button
              onClick={fetchStudents}
              disabled={!batchId || loading}
              className="fee-btn-fetch"
            >
              {loading ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <>
                  <FaSearch /> Fetch
                </>
              )}
            </Button>
          </div>
        </div>

        {errorMsg && (
          <Alert variant="danger" className="no-print">
            {errorMsg}
          </Alert>
        )}

        {students.length > 0 && (
          <div id="fee-summary-print-area">
            {/* Printable Report Header */}
            <div className="fee-summary-print-header">
              {institute?.name && (
                <div className="fee-print-inst-name">{institute.name}</div>
              )}
              <div className="fee-print-title">Student Fee Summary</div>
              <div className="fee-print-meta-row">
                <span className="fee-print-meta-item">
                  <strong>Batch:</strong> {batchDisplayName}
                </span>
                {selectedAcademicYear?.name && (
                  <span className="fee-print-meta-item">
                    <strong>Academic Year:</strong> {selectedAcademicYear.name}
                  </span>
                )}
                <span className="fee-print-meta-item">
                  <strong>Status:</strong> {status}
                </span>
                <span className="fee-print-meta-item">
                  <strong>Students:</strong> {students.length}
                </span>
                <span className="fee-print-meta-item">
                  <strong>Date:</strong>{' '}
                  {new Date().toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </span>
              </div>
            </div>

            {/* Table Container */}
            <div className="fee-summary-table-container">
              <div className="table-responsive">
                <Table bordered hover className="fee-summary-table align-middle">
                  <thead>
                    <tr>
                      <th className="col-sl">#</th>
                      <th
                        className={`col-adno sortable-th ${sortConfig.key === 'admission_no' ? 'active-sort' : ''}`}
                        onClick={() => handleSort('admission_no')}
                        title="Click to sort by Admission Number"
                      >
                        <span className="th-content-center">
                          Ad.No
                          {renderSortIcon('admission_no')}
                        </span>
                      </th>
                      <th
                        className={`col-name sortable-th ${sortConfig.key === 'student_name' ? 'active-sort' : ''}`}
                        onClick={() => handleSort('student_name')}
                        title="Click to sort by Student Name"
                      >
                        <span className="th-content">
                          Student Name
                          {renderSortIcon('student_name')}
                        </span>
                      </th>
                      <th
                        className={`col-status sortable-th ${sortConfig.key === 'status' ? 'active-sort' : ''}`}
                        onClick={() => handleSort('status')}
                        title="Click to sort by Status"
                      >
                        <span className="th-content-center">
                          Status
                          {renderSortIcon('status')}
                        </span>
                      </th>
                      <th
                        className={`col-total sortable-th ${sortConfig.key === 'total_fees' ? 'active-sort' : ''}`}
                        onClick={() => handleSort('total_fees')}
                        title="Click to sort by Total Fees"
                      >
                        <span className="th-content-right">
                          Total Fees
                          {renderSortIcon('total_fees')}
                        </span>
                      </th>
                      <th
                        className={`col-due sortable-th ${sortConfig.key === 'due_fees' ? 'active-sort' : ''}`}
                        onClick={() => handleSort('due_fees')}
                        title="Click to sort by Due Fees"
                      >
                        <span className="th-content-right">
                          Due
                          {renderSortIcon('due_fees')}
                        </span>
                      </th>
                      <th
                        className={`col-paid sortable-th ${sortConfig.key === 'paid_fees' ? 'active-sort' : ''}`}
                        onClick={() => handleSort('paid_fees')}
                        title="Click to sort by Paid Fees"
                      >
                        <span className="th-content-right">
                          Paid Fees
                          {renderSortIcon('paid_fees')}
                        </span>
                      </th>
                      <th
                        className={`col-balance sortable-th ${sortConfig.key === 'due_balance' ? 'active-sort' : ''}`}
                        onClick={() => handleSort('due_balance')}
                        title="Click to sort by Due Balance"
                      >
                        <span className="th-content-right">
                          Due Balance
                          {renderSortIcon('due_balance')}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStudents.map((stu, index) => {
                      const totalFee = parseFloat(stu.total_fees || 0);
                      const dueFee = parseFloat(stu.due_fees || 0);
                      const paidFee = parseFloat(stu.paid_fees || 0);
                      const balance = dueFee - paidFee;

                      return (
                        <tr key={stu.student_id || index}>
                          <td className="col-sl">{index + 1}</td>
                          <td className="col-adno">{stu.admission_no}</td>
                          <td className="col-name">{stu.student_name}</td>
                          <td className="col-status">
                            <span
                              className={`fee-status-badge ${
                                stu.status === 'Active'
                                  ? 'fee-status-active'
                                  : 'fee-status-suspend'
                              }`}
                            >
                              {stu.status}
                            </span>
                          </td>
                          <td className="col-total">
                            ₹{totalFee.toFixed(2)}
                          </td>
                          <td
                            className="col-due"
                            style={{
                              color: dueFee > 0 ? '#dc3545' : 'inherit',
                            }}
                          >
                            ₹{dueFee.toFixed(2)}
                          </td>
                          <td className="col-paid">
                            ₹{paidFee.toFixed(2)}
                          </td>
                          <td
                            className="col-balance"
                            style={{
                              color: balance > 0 ? '#dc3545' : '#28a745',
                            }}
                          >
                            ₹{balance.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th colSpan={4} className="text-end pe-3">
                        Total ({students.length} Students)
                      </th>
                      <th className="col-total">
                        ₹{totals.totalFees.toFixed(2)}
                      </th>
                      <th
                        className="col-due"
                        style={{
                          color: totals.dueFees > 0 ? '#dc3545' : 'inherit',
                        }}
                      >
                        ₹{totals.dueFees.toFixed(2)}
                      </th>
                      <th className="col-paid">
                        ₹{totals.paidFees.toFixed(2)}
                      </th>
                      <th
                        className="col-balance"
                        style={{
                          color:
                            totals.dueBalance > 0 ? '#dc3545' : '#28a745',
                        }}
                      >
                        ₹{totals.dueBalance.toFixed(2)}
                      </th>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </div>

            {/* Summary Statistics Pill for Screen */}
            <div
              className="mt-3 text-center no-print"
              style={{ fontSize: '0.88rem', color: '#6c757d' }}
            >
              <strong>Total Students:</strong> {students.length} •{' '}
              <strong>Total Fees:</strong> ₹{totals.totalFees.toFixed(2)} •{' '}
              <strong>Total Due:</strong> ₹{totals.dueFees.toFixed(2)} •{' '}
              <strong>Total Paid:</strong> ₹{totals.paidFees.toFixed(2)} •{' '}
              <strong>Due Balance:</strong> ₹{totals.dueBalance.toFixed(2)}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default StudentFeeSummary;
